import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import WhatsAppApiModal from '../../../components/Meta/WhatsAppApiModal.tsx';
import AddWabaFormDrawer from '../components/AddWabaFormDrawer';
import { EmptyState, ListPageHeader, SearchField } from '../../../components/ui';
import { ConstantActions } from '../../../Services/_actions/index.js';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider.jsx';
import useWabaApps from '../hooks/useWabaApps';
import useTemplateSx from '../hooks/useTemplateSx';
import { wabaSelectedAppAtom } from '../state/templateAtoms';
import { TEMPLATE_BASE_PATH } from '../constants/templateApi';
import { TEMPLATE_PERMISSIONS } from '../constants/templatePermissions';
import StatusChip from '../components/ui/StatusChip';

function getInitials(name = '') {
  return name
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() || '')
    .join('');
}

export default function WabaAppListPage() {
  return (
    <FeatureThemeProvider>
      <WabaAppListPageContent />
    </FeatureThemeProvider>
  );
}

function WabaAppListPageContent() {
  const styles = useTemplateSx();
  const navigate = useNavigate();
  const constActions = ConstantActions();
  const setSelectedApp = useSetRecoilState(wabaSelectedAppAtom);

  const {
    apps,
    filteredApps,
    search,
    setSearch,
    isLoading,
    loadError,
    liveCount,
    isEmpty,
    isSearchEmpty,
    hasSearchQuery,
    refresh,
  } = useWabaApps();
  const [showAddWabaDrawer, setShowAddWabaDrawer] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [modalApp, setModalApp] = useState(null);

  const canManage = constActions.userHasPermission([
    TEMPLATE_PERMISSIONS.wabaAppList,
    TEMPLATE_PERMISSIONS.metaEmbeddedSignup,
  ]);

  const breadcrumbsData = [
    { label: <IconButton color="primary"><HomeIcon /></IconButton>, href: '/' },
    { label: 'App List' },
  ];

  const openDetails = (app) => {
    setSelectedApp(app);
    navigate(`${TEMPLATE_BASE_PATH}/manager/app-details`);
  };

  const openCreateDrawer = () => {
    setShowAddWabaDrawer(true);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (loadError) {
      return (
        <EmptyState
          icon={<AppsOutlinedIcon fontSize="inherit" />}
          title="Unable to load WhatsApp apps"
          description={loadError}
          action={
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refresh}>
              Try again
            </Button>
          }
        />
      );
    }

    if (isEmpty) {
      return (
        <EmptyState
          icon={<WhatsAppIcon fontSize="inherit" />}
          title="No WhatsApp apps yet"
          description="Connect your first WhatsApp Business app to start sending templates and managing conversations."
          action={
            canManage ? (
              <Button variant="contained" startIcon={<AddIcon />} sx={styles.createButton} onClick={openCreateDrawer}>
                Add WABA
              </Button>
            ) : null
          }
        />
      );
    }

    if (isSearchEmpty) {
      return (
        <EmptyState
          icon={<SearchOffOutlinedIcon fontSize="inherit" />}
          title="No apps match your search"
          description={`We couldn't find any WhatsApp apps matching "${search.trim()}".`}
          action={
            <Button variant="text" onClick={() => setSearch('')}>
              Clear search
            </Button>
          }
        />
      );
    }

    return (
      <Grid container spacing={2} sx={styles.wabaAppsGrid}>
        {filteredApps.map((app) => (
          <Grid item xs={12} md={6} lg={4} key={`${app.waba_id}-${app.wa_phone_number}`}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                cursor: 'pointer',
                height: '100%',
                '&:hover': { boxShadow: 2 },
              }}
              onClick={() => openDetails(app)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  {getInitials(app.app_name) || <WhatsAppIcon />}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                    {app.app_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {app.wa_phone_number}
                  </Typography>
                </Box>
                <StatusChip value={app.is_active ? 'active' : 'inactive'} label={app.is_active ? 'Live' : 'Not Live'} />
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                {app.company_name ? <Chip size="small" label={app.company_name} /> : null}
                {app.waba_id ? <Chip size="small" variant="outlined" label={`WABA: ${app.waba_id}`} /> : null}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {!app.is_active && canManage ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(event) => {
                      event.stopPropagation();
                      setModalApp(app);
                      setShowWhatsAppModal(true);
                    }}
                  >
                    Go Live
                  </Button>
                ) : (
                  <span />
                )}
                <ArrowForwardIcon fontSize="small" color="action" />
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={styles.pageRoot}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(_event, item) => {
          if (item?.href) navigate(item.href);
        }}
      />

      <ListPageHeader
        title="WhatsApp Apps"
        subtitle="Manage connected WhatsApp Business apps for your organisation."
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={refresh} aria-label="Refresh apps" disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
            {canManage ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={styles.createButton}
                onClick={openCreateDrawer}
              >
                Add WABA
              </Button>
            ) : null}
          </Box>
        }
      />

      {!isEmpty && !loadError ? (
        <Box sx={styles.wabaAppsToolbar}>
          <SearchField
            placeholder="Search by app name, phone, or WABA ID..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            size="sm"
            disabled={isLoading}
            icon={<SearchIcon sx={{ fontSize: 18 }} />}
            sx={styles.wabaAppsSearch}
          />
          {!isLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
              {liveCount} live · {hasSearchQuery ? `${filteredApps.length} of ${apps.length} apps` : `${apps.length} apps`}
            </Typography>
          ) : null}
        </Box>
      ) : null}

      <Box sx={styles.wabaAppsSurface}>
        {renderContent()}
      </Box>

      <AddWabaFormDrawer
        open={showAddWabaDrawer}
        onClose={() => setShowAddWabaDrawer(false)}
        onCreated={() => refresh()}
      />

      {showWhatsAppModal ? (
        <WhatsAppApiModal
          onClose={() => {
            setShowWhatsAppModal(false);
            setModalApp(null);
            refresh();
          }}
          appName={modalApp?.app_name ?? null}
        />
      ) : null}
    </Box>
  );
}
